import * as R from "ramda";
import initialQueueState from "../models/queue-state";
import Types from "../actions/types";

// Normalize queue items to ensure they have all required fields (for backwards compatibility)
const normalizeQueueItem = (item) => {
	if (!item) return null;
	return {
		...item,
		failedCount: item.failedCount !== undefined ? item.failedCount : 0,
		lastError: item.lastError !== undefined ? item.lastError : null,
		isPermanentFailure: item.isPermanentFailure !== undefined ? item.isPermanentFailure : false,
		maxRetries: item.maxRetries !== undefined ? item.maxRetries : 5,
		processedBy: item.processedBy !== undefined ? item.processedBy : undefined,
	};
};

// Normalize all items in the queue list
const normalizeQueueList = (list) => {
	if (!list || !Array.isArray(list)) return [];
	return list.map(normalizeQueueItem).filter(item => item !== null);
};

const sendSucceeded = (state, action) => {
	if (!Array.isArray(state.list)) {
		state.list = [];
	} else {
		state.list = R.filter(
			(item) => item && item.id !== action.payload.queueId,
			state.list
		);
	}
	return state;
};

const sendFailed = (state, action) => {
	// Handle both old format (just id string) and new format (object with id, error, isPermanent)
	const payload = action.payload;
	const id = typeof payload === 'string' ? payload : (payload?.id || payload);
	const error = typeof payload === 'string' ? null : (payload?.error || null);
	const isPermanent = typeof payload === 'string' ? false : (payload?.isPermanent || false);
	
	if (!Array.isArray(state.list)) {
		state.list = [];
	} else {
		state.list.forEach((item, index) => {
			if (item && item.id === id) {
				// Ensure item has required fields before accessing
				const normalizedItem = normalizeQueueItem(item);
				normalizedItem.failedCount = (normalizedItem.failedCount || 0) + 1;
				normalizedItem.lastError = error;
				normalizedItem.isPermanentFailure = isPermanent;
				normalizedItem.processedBy = undefined;
				// Update the item in the list
				state.list[index] = normalizedItem;
			}
		});
	}
	return state;
};

const itemRetried = (state, action) => {
	const id = action.payload;
	if (!Array.isArray(state.list)) {
		state.list = [];
	} else {
		state.list.forEach((item, index) => {
			if (item && item.id === id) {
				// Normalize and reset failure state for retry
				const normalizedItem = normalizeQueueItem(item);
				normalizedItem.failedCount = 0;
				normalizedItem.lastError = null;
				normalizedItem.isPermanentFailure = false;
				normalizedItem.processedBy = undefined;
				state.list[index] = normalizedItem;
			}
		});
	}
	return state;
};

const itemRemoved = (state, action) => {
	const id = action.payload;
	if (!Array.isArray(state.list)) {
		state.list = [];
	} else {
		state.list = R.filter((item) => item && item.id !== id, state.list);
	}
	return state;
};

const sendRequested = (state, action) => {
	return state;
};

const itemAdded = (state, action) => {
	// Ensure the item has all required fields with defaults
	const item = normalizeQueueItem(action.payload);
	if (item) {
		if (!Array.isArray(state.list)) {
			state.list = [];
		}
		state.list.push(item);
	}
	return state;
};

const itemsRestored = (state, action) => {
	const incoming = Array.isArray(action.payload) ? action.payload : [];
	if (!Array.isArray(state.list)) {
		state.list = [];
	}
	const existingIds = new Set(state.list.filter((i) => i && i.id).map((i) => i.id));
	incoming.forEach((raw) => {
		const item = normalizeQueueItem(raw);
		if (!item || !item.id || existingIds.has(item.id)) return;
		// Fresh auth after logout — allow a clean retry of previously auth-failed items.
		item.failedCount = 0;
		item.lastError = null;
		item.isPermanentFailure = false;
		item.processedBy = undefined;
		state.list.push(item);
		existingIds.add(item.id);
	});
	return state;
};

const networkChanged = (state, action) => {
	state.hasNetwork = action.payload;
	return state;
};

const resetProcessing = (state, action) => {
	if (Array.isArray(state.list)) {
		state.list.forEach((item) => {
			if (item) {
				item.processedBy = undefined;
			}
		});
	}
	return state;
};

const statusReducer = (state = initialQueueState, action) => {
	// Ensure state has required structure - handle malformed state gracefully
	if (!state || typeof state !== 'object') {
		state = initialQueueState;
	}
	if (!Array.isArray(state.list)) {
		state = { ...initialQueueState, ...state, list: [] };
	}

	switch (action.type) {
		case Types.QUEUE_NETWORK_CHANGED:
			return networkChanged(R.clone(state), action);
		case Types.QUEUE_ITEM_ADDED:
			return itemAdded(R.clone(state), action);
		case Types.QUEUE_ITEMS_RESTORED:
			return itemsRestored(R.clone(state), action);
		case Types.QUEUE_SEND_FAILED:
			return sendFailed(R.clone(state), action);
		case Types.QUEUE_SEND_SUCCEEDED:
			return sendSucceeded(R.clone(state), action);
		case Types.QUEUE_SEND_REQUESTED:
			return sendRequested(R.clone(state), action);
		case Types.QUEUE_ITEM_RETRY:
			return itemRetried(R.clone(state), action);
		case Types.QUEUE_ITEM_REMOVED:
			return itemRemoved(R.clone(state), action);
		case Types.FRAME_INITIALISE_REQUESTED:
			return resetProcessing(R.clone(state), action);
		default:
			return state;
	}
};

export default statusReducer;
